import { useState } from 'react';

import type { DevelopmentDetailDataModel, DevelopmentSummaryModel } from '@/domain/models/development.js';
import type { BusinessProcessResponse, ProjectUserResponse } from '@/domain/models/project.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import type { Complexity, DevelopmentCategory } from '@/shared/utils/format-status.js';

export type EditFormState = {
    title: string;
    description: string;
    businessProcessId: string;
    consultorId: string;
    keyUserId: string;
    linkedDev: DevelopmentSummaryModel | null;
    complexity: Complexity | '';
    hours: string;
    category: DevelopmentCategory | '';
};

const EMPTY_FORM: EditFormState = {
    title: '',
    description: '',
    businessProcessId: '',
    consultorId: '',
    keyUserId: '',
    linkedDev: null,
    complexity: '',
    hours: '',
    category: ''
};

type UpdateDevelopmentPayload = {
    title?: string;
    description?: string;
    businessProcessId?: string | null;
    consultorId?: string;
    usuarioChaveId?: string;
    linkedDevelopmentId?: string | null;
    functionalComplexity?: Complexity | null;
    functionalHours?: number | null;
    category?: DevelopmentCategory | null;
};

export function useDevelopmentEditOptions(
    projectController: ProjectController,
    devController: DevelopmentController,
    updateDevelopment: (payload: UpdateDevelopmentPayload) => Promise<string | null>
) {
    const [form, setForm] = useState<EditFormState>(EMPTY_FORM);
    const [businessProcesses, setBusinessProcesses] = useState<BusinessProcessResponse[]>([]);
    const [consultores, setConsultores] = useState<ProjectUserResponse[]>([]);
    const [keyUsers, setKeyUsers] = useState<ProjectUserResponse[]>([]);
    const [linkableDevs, setLinkableDevs] = useState<DevelopmentSummaryModel[]>([]);
    const [saving, setSaving] = useState(false);

    const setField = <K extends keyof EditFormState>(key: K, value: EditFormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const open = (dev: DevelopmentDetailDataModel, linkOnly = false) => {
        setForm({
            title: dev.title,
            description: dev.description ?? '',
            businessProcessId: dev.businessProcessId ?? '',
            consultorId: dev.consultorId ?? '',
            keyUserId: dev.usuarioChaveId ?? '',
            linkedDev: null,
            complexity: dev.functionalComplexity ?? '',
            hours: dev.functionalHours !== null ? String(dev.functionalHours) : '',
            category: dev.category ?? ''
        });
        // Perfis não-CF só editam o vínculo — nenhum dos outros campos aparece no diálogo, então
        // não há por que buscar processos de negócio/consultores/usuários-chave para eles.
        if (!linkOnly) {
            projectController.listActiveBusinessProcesses(dev.projectId).then((r) =>
                r.fold(
                    () => setBusinessProcesses([]),
                    (bps) => setBusinessProcesses(bps)
                )
            );
            projectController.listProjectUsers(dev.projectId, 'CF').then((r) =>
                r.fold(
                    () => setConsultores([]),
                    (u) => setConsultores(u)
                )
            );
            projectController.listProjectUsers(dev.projectId, 'UC').then((r) =>
                r.fold(
                    () => setKeyUsers([]),
                    (u) => setKeyUsers(u)
                )
            );
        }
        devController.loadDevelopments({ projectId: dev.projectId, limit: 100 }).then((r) =>
            r.fold(
                () => setLinkableDevs([]),
                (d) => {
                    setLinkableDevs(d.data);
                    setForm((prev) => ({ ...prev, linkedDev: d.data.find((x) => x.id === dev.linkedDevelopmentId) ?? null }));
                }
            )
        );
    };

    const save = async (mode: 'full' | 'partial' | 'link'): Promise<string | null> => {
        const linkedDevId = form.linkedDev ? form.linkedDev.id : null;
        let payload: UpdateDevelopmentPayload;
        if (mode === 'link') {
            payload = { linkedDevelopmentId: linkedDevId };
        } else if (mode === 'full') {
            payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                businessProcessId: form.businessProcessId || null,
                consultorId: form.consultorId || undefined,
                usuarioChaveId: form.keyUserId || undefined,
                linkedDevelopmentId: linkedDevId,
                functionalComplexity: form.complexity || null,
                functionalHours: form.hours ? Number(form.hours) : null,
                category: form.category || null
            };
        } else {
            payload = {
                businessProcessId: form.businessProcessId || null,
                consultorId: form.consultorId || undefined,
                usuarioChaveId: form.keyUserId || undefined,
                category: form.category || null
            };
        }
        setSaving(true);
        try {
            return await updateDevelopment(payload);
        } finally {
            setSaving(false);
        }
    };

    return { form, setField, businessProcesses, consultores, keyUsers, linkableDevs, open, saving, save };
}
